/**
 * Real-time clip quality gate (FSD §4.3, `Rule-based`, edge) — samples the live viewfinder while
 * a clip records and judges the whole clip at stop. Forked in spirit from the droom's
 * useViewfinderExposure (the honest pre-shot cue) and imageQuality (the post-shot gate).
 *
 * Honesty contract:
 *   • The live cue reports ONLY what is cheaply, honestly detectable on-device — exposure and a
 *     coarse focus measure. null = "no claim" (neutral frame), never a fabricated "looks good".
 *   • The verdict at stop is a UX gate with named reasons and named thresholds (thresholds.ts).
 *     `INSUFFICIENT` never passes: a gate that ran on too few samples reports NOT_EVALUATED and the
 *     UI says so — it does not silently pass and it does not fake a refusal reason.
 *   • US-03482 G4 / F11 (iPhone black-video RCA, 2026-11-03): a recording under EMPTY_CLIP_MIN_BYTES
 *     (10 240, the be's SV_CLIP_EMPTY floor) is EMPTY — never sealed, the representative records again (a
 *     5-byte clip had been sealed as NOT_EVALUATED). A normal-size file whose frames could not be sampled
 *     stays NOT_EVALUATED ("use anyway"), as before: the bytes are the proof of an empty file.
 *   • No pixels leave this module; nothing is logged or posted.
 */

import { useEffect, useRef, useState } from 'react';
import { frameMetrics, median } from './frameMetrics';
import { CLIP_GATES, GATE_VERSION, LUMA_MAX, LUMA_MIN } from './thresholds';

export interface ClipSample {
  readonly t: number;
  readonly meanLuma: number;
  readonly laplacianVar: number;
}

export type ClipCue = 'too-dark' | 'too-bright' | 'blurry' | null;

export type ClipVerdictReason =
  | 'TOO_SHORT'
  | 'TOO_DARK'
  | 'TOO_BRIGHT'
  | 'TOO_BLURRY'
  | 'NOT_EVALUATED'
  /** US-03482 G4 / F11: nothing was recorded — the file is under EMPTY_CLIP_MIN_BYTES. Never sealed. */
  | 'EMPTY'
  /** US-03482 round 3 (A4): the camera stopped sending a picture while the clip recorded. Never sealed. */
  | 'CAMERA_STOPPED';

export interface ClipVerdict {
  readonly ok: boolean;
  readonly reason: ClipVerdictReason | null;
  /** Deck key for the blocking copy; null when ok. */
  readonly messageKey: string | null;
  /** True only for NOT_EVALUATED — the representative may proceed with the honest label. */
  readonly mayProceedUnevaluated: boolean;
  readonly metrics: {
    readonly durationS: number;
    readonly samples: number;
    readonly darkRatio: number;
    readonly brightRatio: number;
    readonly medianLaplacianVar: number;
  };
  /** The operating points this verdict was judged against (logged with the shot). */
  readonly thresholds: {
    readonly minDurationS: number;
    readonly lumaMin: number;
    readonly lumaMax: number;
    readonly badExposureRatioMax: number;
    readonly minMedianLaplacianVar: number;
    readonly version: string;
  };
}

/** G4 / F11: a recording below this many bytes holds no usable video — the be's own floor (SV_CLIP_EMPTY). */
export const EMPTY_CLIP_MIN_BYTES = 10_240;

export function liveCue(sample: ClipSample): ClipCue {
  if (sample.meanLuma < LUMA_MIN) return 'too-dark';
  if (sample.meanLuma > LUMA_MAX) return 'too-bright';
  if (sample.laplacianVar < CLIP_GATES.minMedianLaplacianVar) return 'blurry';
  return null;
}

export function assessClip(
  samples: readonly ClipSample[],
  durationS: number,
  shot: { readonly minDurationS: number },
  /** G4: the bytes the recorder delivered; omitted by callers that only judge the frames. */
  totalBytes?: number,
): ClipVerdict {
  const thresholds = {
    minDurationS: shot.minDurationS,
    lumaMin: LUMA_MIN,
    lumaMax: LUMA_MAX,
    badExposureRatioMax: CLIP_GATES.badExposureRatioMax,
    minMedianLaplacianVar: CLIP_GATES.minMedianLaplacianVar,
    version: GATE_VERSION,
  };
  const n = samples.length;
  const dark = samples.filter((s) => s.meanLuma < LUMA_MIN).length;
  const bright = samples.filter((s) => s.meanLuma > LUMA_MAX).length;
  const medianLap = median(samples.map((s) => s.laplacianVar));
  const metrics = {
    durationS,
    samples: n,
    darkRatio: n ? dark / n : 0,
    brightRatio: n ? bright / n : 0,
    medianLaplacianVar: medianLap,
  };
  const blocked = (reason: ClipVerdictReason, messageKey: string, mayProceed = false): ClipVerdict => ({
    ok: false,
    reason,
    messageKey,
    mayProceedUnevaluated: mayProceed,
    metrics,
    thresholds,
  });

  // Duration first — the cheapest fact and the one the representative can fix instantly.
  if (durationS < shot.minDurationS) return blocked('TOO_SHORT', 'sv.gate.clip.short');
  // F11: only a truly empty FILE is EMPTY (never sealed); unsampled frames alone stay NOT_EVALUATED below.
  if (totalBytes !== undefined && totalBytes < EMPTY_CLIP_MIN_BYTES) return blocked('EMPTY', 'sv.gate.clip.empty');
  if (n < CLIP_GATES.minSamples) return blocked('NOT_EVALUATED', 'sv.gate.clip.notEvaluated', true);
  // Exposure before sharpness: bad light depresses the Laplacian and would name the wrong fix.
  if (metrics.darkRatio > CLIP_GATES.badExposureRatioMax) return blocked('TOO_DARK', 'sv.gate.clip.dark');
  if (metrics.brightRatio > CLIP_GATES.badExposureRatioMax) return blocked('TOO_BRIGHT', 'sv.gate.clip.bright');
  if (medianLap < CLIP_GATES.minMedianLaplacianVar) return blocked('TOO_BLURRY', 'sv.gate.clip.blurry');
  return { ok: true, reason: null, messageKey: null, mayProceedUnevaluated: false, metrics, thresholds };
}

/**
 * US-03482 round 3 (A4): a take the camera stopped under is not judged — whatever its frames show, they stop where the
 * camera did — and never sealed: the representative records the step again once the camera is back. The metrics and
 * operating points stay those of `measured`, for the record.
 */
export function cameraStoppedVerdict(measured: ClipVerdict): ClipVerdict {
  return { ...measured, ok: false, reason: 'CAMERA_STOPPED', messageKey: 'sv.gate.clip.cameraStopped', mayProceedUnevaluated: false };
}

/**
 * Sample the live viewfinder at ≤ 2/s while `enabled`. Returns the live cue (two consecutive
 * identical reads before the UI moves — no strobing at a band edge) and a stable getter for the
 * samples taken so far. `reset()` clears the buffer at the start of every recording attempt.
 */
export function useClipSampler(
  getVideo: () => HTMLVideoElement | null,
  enabled: boolean,
): { readonly cue: ClipCue; readonly samples: () => readonly ClipSample[]; readonly reset: () => void } {
  const [cue, setCue] = useState<ClipCue>(null);
  const buffer = useRef<ClipSample[]>([]);
  const lastRaw = useRef<ClipCue>(null);

  useEffect(() => {
    if (!enabled) {
      setCue(null);
      lastRaw.current = null;
      return undefined;
    }
    const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
    const ctx = canvas?.getContext('2d', { willReadFrequently: true }) ?? null;
    if (!canvas || !ctx) return undefined; // cannot sample — no claim; the verdict says NOT_EVALUATED

    const timer = window.setInterval(() => {
      const el = getVideo();
      if (!el || el.readyState < 2 || !el.videoWidth || !el.videoHeight) return;
      try {
        const w = CLIP_GATES.sampleWidth;
        const h = Math.max(2, Math.round((el.videoHeight * w) / el.videoWidth));
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(el, 0, 0, w, h);
        const { meanLuma, laplacianVar } = frameMetrics(ctx.getImageData(0, 0, w, h).data, w, h);
        const sample: ClipSample = { t: Date.now(), meanLuma, laplacianVar };
        buffer.current.push(sample);
        const raw = liveCue(sample);
        setCue(raw === lastRaw.current ? raw : null);
        lastRaw.current = raw;
      } catch {
        lastRaw.current = null;
        setCue(null);
      }
    }, CLIP_GATES.sampleIntervalMs);
    return () => window.clearInterval(timer);
  }, [enabled, getVideo]);

  const samples = useRef(() => buffer.current as readonly ClipSample[]).current;
  const reset = useRef(() => {
    buffer.current = [];
  }).current;
  return { cue, samples, reset };
}
